import { ISkeletonData, Spine, SpineDebugRenderer } from "./PixiSpineRuntime";
import * as PIXI from "pixi.js";
import { Credit, FrontendErrorCode, PlayWindow } from "./api";
import { IReadOnlyResourceManager } from "./ResourceManager/ResourceManager";
import { SpriteResource } from "./ResourceManager/SpriteResource";
import { service } from "./services";
import { Settings } from "./Settings";
import { CanvasSprite } from "./slot/Sprite/CanvasSprite";
import gsap from "gsap";
import { DecodedImageResource } from "./ResourceManager/ImageResource";

// const originalConsoleLog = console.log;
// console.log = function(...args)
// {
// 	const types = args.map(arg => Object.getPrototypeOf(arg).constructor.name) as string[];
// 	originalConsoleLog.apply(console, args);
// 	originalConsoleLog("Logged types:", types);

// 	if (types.includes("Spine") || types.includes("SpineBase")) {
// 		debugger;
// 	}
// }


export type Millis = number;

export function now(): Millis {
	return performance.now();
}

export class Timing {
	public static Now(): Millis {
		return performance.now();
	}

	public static Timeouted(lastOccurence: number, timeout: number, customNow?: number): boolean {

		const now = customNow ?? Timing.Now();
		if (lastOccurence > now) return false;

		let duration: number = now - lastOccurence;

		// todo overflow?
		//if (duration < 0) duration += long.MaxValue;

		return duration > timeout;
	}
}

export class LogGroup {
	protected path: string[] = [];
	protected name: string = "";

	public static readonly Global: LogGroup = new LogGroup("Global");

	constructor(path: string | string[]) {
		if (path instanceof Array) {
			this.path = path;
		}
		else {
			this.path = [path];
		}

		this.name = this.path.join("|");
	}

	public Add(path: string | string[]): LogGroup {
		let newPath: string[] = [].concat(this.path);

		if (path instanceof Array) {
			newPath = newPath.concat(path);
		}
		else {
			newPath.push(path);
		}

		return new LogGroup(newPath);
	}

	public get Name() {
		return this.name;
	}
}

function stackTrace() {
	var err = new Error();
	return err.stack;
}

export class Log {

	public static Debug(source: string | LogGroup, message: string, ...data: any): void {
		if (Settings.ConsoleLogging) {
			let prefix: string;
			if (source instanceof LogGroup) {
				prefix = "+" + now().toFixed(0) + " DBG [" + source.Name + "] ";
			}
			else {
				prefix = "+" + now().toFixed(0) + " DBG [" + source + "] ";
			}


			//console.log("%c" + prefix + message, "", ...data, stackTrace());
			console.log("%c" + prefix + message, "", ...data);
		}
	}

	public static Important(source: string | LogGroup, message: string, ...data: any[]): void {
		if (Settings.ConsoleLogging) {
			let prefix: string;
			if (source instanceof LogGroup) {
				prefix = "+" + now().toFixed(0) + " DBG [" + source.Name + "] ";
			}
			else {
				prefix = "+" + now().toFixed(0) + " DBG [" + source + "] ";
			}

			console.warn("%c" + prefix + message, "", ...data);
		}
	}

	public static Error(source: string | LogGroup, message: string, ...data: any[]): void {
		if (Settings.ConsoleLogging) {
			let prefix: string;
			if (source instanceof LogGroup) {
				prefix = "+" + now().toFixed(0) + " ERR [" + source.Name + "] ";
			}
			else {
				prefix = "+" + now().toFixed(0) + " ERR [" + source + "] ";
			}

			console.error("%c" + prefix + message + "\n" + (new Error()).stack, "color: #9F6000; background-color: #FEEFB3", ...data);
		}
	}

	public static Critical(source: string | LogGroup, message: string, ...data: any[]): void {

		let prefix: string;
		if (source instanceof LogGroup) {
			prefix = "+" + now().toFixed(0) + " CRITICAL [" + source.Name + "] ";
		}
		else {
			prefix = "+" + now().toFixed(0) + " CRITICAL [" + source + "] ";
		}

		if (Settings.ConsoleLogging) {
			console.error("%c" + prefix + message + "\n" + (new Error()).stack, "color: #9F6000; background-color: #FEEFB3", ...data);
		}

		let error = new Error();

		let errorData = {
			source: prefix,
			message: message,
			data: data,
			error: JSON.stringify(error, Object.getOwnPropertyNames(error))
		};

		void service?.api?.frontendError?.(FrontendErrorCode.CriticalFrontendError, JSON.stringify(errorData, Object.getOwnPropertyNames(errorData)));
	}
}


export type GetTextCallback = (success: boolean, status: number, response: string) => void;

export class Get {
	public static Text(url: string, finishCallback: GetTextCallback) {
		let request: XMLHttpRequest = new XMLHttpRequest();
		request.open("GET", url, true);

		request.onload = function () {
			if (request.status >= 200 && request.status < 400) {
				finishCallback(true, request.status, request.responseText);
			}
			else {
				finishCallback(false, request.status, request.responseText);
			}
		}

		request.onerror = function () {
			finishCallback(false, 0, request.responseText);
		}

		request.send();
	}
}

export enum HorizontalAlign {
	Left,
	Center,
	Right
}

export enum VerticalAlign {
	Top,
	Center,
	Bottom
}

export class Geometry {
	public static FitRect(innerRect: Rect, outerRect: Rect, horizontalAlign: HorizontalAlign = HorizontalAlign.Left, verticalAlign: VerticalAlign = VerticalAlign.Top): Rect {

		//let xratio = outerRect.width / innerRect.width;
		//let yratio = outerRect.height / innerRect.height;
		//Math.min(xratio, yratio);

		let minRatio = Geometry.FitRectRatio(innerRect, outerRect);

		let newWidth = innerRect.width * minRatio;
		let newHeight = innerRect.height * minRatio;

		let newx = outerRect.x;
		let newy = outerRect.y;


		// horizontal alignment
		if (horizontalAlign == HorizontalAlign.Left) {
			// already left
		}
		else if (horizontalAlign == HorizontalAlign.Center) {
			newx += (outerRect.width - newWidth) / 2;
		}
		else if (horizontalAlign == HorizontalAlign.Right) {
			newx += outerRect.width - newWidth;
		}

		// vertical alignment
		if (verticalAlign == VerticalAlign.Top) {
			// already top
		}
		else if (verticalAlign == VerticalAlign.Center) {
			newy += (outerRect.height - newHeight) / 2;
		}
		else if (verticalAlign == VerticalAlign.Bottom) {
			newy += outerRect.height - newHeight;
		}

		return new Rect(newx, newy, newWidth, newHeight);
	}

	public static FitRectRatio(innerRect: IRect, outerRect: IRect): number {
		let xratio = outerRect.width / innerRect.width;
		let yratio = outerRect.height / innerRect.height;

		let minRatio = Math.min(xratio, yratio);
		return minRatio;
	}

	// inner rect covers outer rect
	public static CoverRect(innerRect: Rect, outerRect: Rect, horizontalAlign: HorizontalAlign = HorizontalAlign.Left, verticalAlign: VerticalAlign = VerticalAlign.Top): Rect {

		let xratio = outerRect.width / innerRect.width;
		let yratio = outerRect.height / innerRect.height;

		let minRatio = Math.max(xratio, yratio);

		let newWidth = innerRect.width * minRatio;
		let newHeight = innerRect.height * minRatio;

		let newx = outerRect.x;
		let newy = outerRect.y;

		// horizontal alignment
		if (horizontalAlign == HorizontalAlign.Left) {
			// already left
		}
		else if (horizontalAlign == HorizontalAlign.Center) {
			newx += (outerRect.width - newWidth) / 2;
		}
		else if (horizontalAlign == HorizontalAlign.Right) {
			newx += outerRect.width - newWidth;
		}

		// vertical alignment
		if (verticalAlign == VerticalAlign.Top) {
			// already top
		}
		else if (verticalAlign == VerticalAlign.Center) {
			newy += (outerRect.height - newHeight) / 2;
		}
		else if (verticalAlign == VerticalAlign.Bottom) {
			newy += outerRect.height - newHeight;
		}

		return new Rect(newx, newy, newWidth, newHeight);;
	}

	public static ScaleRect(rect: Rect, scalex: number, scaley: number): Rect {

		// default origin is center
		let scaleOrigin: Point = rect.Center();

		let scaleFactorX = scalex;
		let scaleFactorY = scaley;

		let x = scaleOrigin.x + (rect.x - scaleOrigin.x) * scaleFactorX;
		let y = scaleOrigin.y + (rect.y - scaleOrigin.y) * scaleFactorY;
		let w = rect.width * scaleFactorX;
		let h = rect.height * scaleFactorY;

		return new Rect(x, y, w, h);
	}
}


export class Point {
	public x: number = 0;
	public y: number = 0;

	public constructor(x: number = 0, y: number = 0) {
		this.x = x;
		this.y = y;
	}

	public MultiplyConstant(c: number): Point {
		return new Point(this.x * c, this.y * c);
	}

	public Multiply(point: Point): Point {
		return new Point(this.x * point.x, this.y * point.y);
	}

	public Add(point: Point): Point {
		return new Point(this.x + point.x, this.y + point.y);
	}
}

export class Vec2d {
	public x: number = 0;
	public y: number = 0;

	public constructor(x: number = 0, y: number = 0) {
		this.x = x;
		this.y = y;
	}
}

export interface ISize {
	Width: number;
	Height: number;
}

export class Size implements ISize {
	public Width: number = 0;
	public Height: number = 0;

	public constructor(width: number = 0, height: number = 0) {
		this.Width = width;
		this.Height = height;
	}

	public static FromPlayWindow(playWindow: PlayWindow): Size {
		return new Size(playWindow.Width, playWindow.Height);
	}

	public Scale(scale: number): Size {
		return new Size(this.Width * scale, this.Height * scale);
	}
}

export interface IRect {
	x: number;
	y: number;
	width: number;
	height: number;
}

export class Rect implements IRect {
	public readonly x: number = 0;
	public readonly y: number = 0;
	public readonly width: number = 0;
	public readonly height: number = 0;

	public readonly centerx: number = 0;
	public readonly centery: number = 0;
	protected readonly center: Point;


	public constructor(x: number = 0, y: number = 0, width: number = 0, height: number = 0) {
		this.x = x;
		this.y = y;
		this.width = width;
		this.height = height;

		this.centerx = this.x + this.width / 2;
		this.centery = this.y + this.height / 2;

		this.center = new Point(this.centerx, this.centery);
	}

	public static Construct(p: Point, s: Size): Rect {
		return new Rect(p.x, p.y, s.Width, s.Height);
	}

	public static FromCoordiantes(x1: number, y1: number, x2: number, y2: number) {
		return new Rect(x1, y1, x2 - x1, y2 - y1);
	}

	public static FromSize(size: Size) {
		return new Rect(0, 0, size.Width, size.Height);
	}


	public Point(): Point {
		return new Point(this.x, this.y);
	}

	public Size(): Size {
		return new Size(this.width, this.height);
	}

	public Move(forx: number, fory: number): Rect {
		return new Rect(this.x + forx, this.y + fory, this.width, this.height);
	}

	public SetPosition(x: number, y: number): Rect {
		return new Rect(x, y, this.width, this.height);
	}

	public Multiply(size: Size): Rect {
		return new Rect(
			this.x * size.Width,
			this.y * size.Height,
			this.width * size.Width,
			this.height * size.Height
		);
	}

	public Inflate(size: Size | number): Rect {
		if (size instanceof Size) {
			return new Rect(
				this.x - size.Width,
				this.y - size.Height,
				this.width + (2 * size.Width),
				this.height + (2 * size.Height)
			);
		}
		else // size is number
		{
			return new Rect(
				this.x - size,
				this.y - size,
				this.width + (2 * size),
				this.height + (2 * size)
			);
		}
	}

	public Scale(scalex: number, scaley: number): Rect {
		// default origin is center
		let scaleOrigin: Point = this.Center();


		let x = scaleOrigin.x + (this.x - scaleOrigin.x) * scalex;
		let y = scaleOrigin.y + (this.y - scaleOrigin.y) * scaley;
		let w = this.width * scalex;
		let h = this.height * scaley;

		return new Rect(x, y, w, h);
	}

	public Resize(size: Size | number): Rect {
		if (size instanceof Size) {
			return new Rect(
				this.x,
				this.y,
				this.width + size.Width,
				this.height + size.Height
			);
		}
		else // size is number
		{
			return new Rect(
				this.x,
				this.y,
				this.width + size,
				this.height + size
			);
		}
	}

	public Area(): number {
		return this.width * this.height;
	}

	public Center(): Point {
		return this.center;
	}

	public toString(): string {
		return "x: " + this.x + ", y: " + this.y + ", width: " + this.width + ", height: " + this.height;
	}
}

export function PadLeft(contents: string, padding: string, totalLength: number) {
	let result: string = contents;

	while (result.length + padding.length <= totalLength) {
		result = padding + result;
	}

	return result;
}


export function addMultiEventListener(eventSourceObject: any, events: string[], callback: any) {
	let e: string[] = [];
	e = events;


	for (let i = 0; i < e.length; i++) {
		eventSourceObject.addEventListener(e[i], callback);
	}

	//events.forEach(e., function (e)
	//{
	//    eventSourceObject.addEventListener(e, callback);
	//});
}


export interface ITouchParameters {

}

export type TouchEventCallback = (ITouchParameters) => void

export function AddTouchStartEventListener(element: HTMLElement, callback: TouchEventCallback) {

	let mouseOrTouchEventHandler = (ev: MouseEvent | TouchEvent) => {
		callback({});

		ev.stopPropagation();
	}

	element.addEventListener("mousedown", mouseOrTouchEventHandler);
}

export class Color {
	public r: number = 0;
	public g: number = 0;
	public b: number = 0;
	public a: number = 0;

	public static readonly RGB_COLOR_REGEX: RegExp = /\((\d+),\s*(\d+),\s*(\d+)(,\s*(\d*.\d*))?\)/;

	constructor() {

	}

	public static FromRGBA(r: number, g: number, b: number, a: number): Color {
		let c: Color = new Color();
		c.r = Math.round(r);
		c.g = Math.round(g);
		c.b = Math.round(b);
		c.a = a;

		return c;
	}

	public static FromCSS(c: string): Color {
		const res = Color.RGB_COLOR_REGEX.exec(c);
		let r = parseInt(res[1], 10);
		let g = parseInt(res[2], 10);
		let b = parseInt(res[3], 10);
		let a = res[5] ? parseFloat(res[5]) : 1;

		let color = Color.FromRGBA(r, g, b, a);

		return color;
	}


	/*
	//constructor(r?: string|number, g?: number, b?: number)
	constructor(r?: string|number, g?: number, b?: number, a?: number)
	constructor(colorStr?: string) {
		if (typeof r === 'string') {
			r = r.trim();
			if (r.indexOf('#') === 0) {
				r = r.substr(r.indexOf('#') + 1);
				this.r = parseInt(r.substr(0, 2), 16);
				this.g = parseInt(r.substr(2, 2), 16);
				this.b = parseInt(r.substr(4, 2), 16);
			} else if (r.indexOf('rgb') === 0) {
				const res = RGB_COLOR_REGEX.exec(r);
				this.r = parseInt(res[1], 10);
				this.g = parseInt(res[2], 10);
				this.b = parseInt(res[3], 10);
				this.a = res[5] ? parseFloat(res[5]) : 1;
			}
		} else {
			this.r = r;
			this.g = g;
			this.b = b;
			this.a = a || 1;
		}
	}
	*/

	toHex() {
		return '#' + PadLeft(this.r.toString(16), "0", 2) + PadLeft(this.g.toString(16), "0", 2) + PadLeft(this.b.toString(16), "0", 2);
	}

	toRgb() {
		return `rgb(${this.r}, ${this.g}, ${this.b})`;
	}

	toRgba() {
		return `rgba(${this.r}, ${this.g}, ${this.b}, ${this.a})`;
	}
}

export function MakeColorGradient(frequency1: number, frequency2: number, frequency3: number,
	phase1: number, phase2: number, phase3: number,
	center: number = 128, width: number = 127, len: number = 50, alpha: number): Color[] {
	if (center == undefined) center = 128;
	if (width == undefined) width = 127;
	if (len == undefined) len = 50;

	let colors: Color[] = [];

	for (let i = 0; i < len; ++i) {
		let red = Math.sin(frequency1 * i + phase1) * width + center;
		let grn = Math.sin(frequency2 * i + phase2) * width + center;
		let blu = Math.sin(frequency3 * i + phase3) * width + center;

		colors.push(Color.FromRGBA(red, grn, blu, alpha));
	}

	return colors;
}


// https://www.w3schools.com/howto/howto_js_fullscreen.asp
export function EnterFullscreen(): void {

}

// https://www.w3schools.com/howto/howto_js_fullscreen.asp
export function ExitFullscreen(): void {

}

export type Style = Partial<CSSStyleDeclaration>;

// elem can be single element or list of elements
export function SetStyles(elem: Element | NodeList | Element[], newStyle: Style) {
	if (elem instanceof Element) {
		for (let key of Object.keys(newStyle)) {
			(elem as HTMLElement).style[key] = newStyle[key];
		}
	}
	else if (elem instanceof NodeList) {
		for (let i = 0; i < elem.length; i++) {
			SetStyles(elem[i] as HTMLElement, newStyle);
		}
	}
	else {
		for (let i = 0; i < elem.length; i++) {
			SetStyles(elem[i] as HTMLElement, newStyle);
		}
	}
}

export interface ElementAttributes {
	[name: string]: string;
}

export interface CreateElementParams<K extends keyof HTMLElementTagNameMap> {
	classes?: string[],
	id?: string,
	attrs?: ElementAttributes,
	style?: Partial<CSSStyleDeclaration>,
	parentToAppend?: Node,
	parentToPrepend?: Node,
	childToAppend?: Node,
	innerHTML?: string,
	mousedown?: MouseDownCallback,
	mouseup?: MouseUpCallback
}

export function CreateElement<K extends keyof HTMLElementTagNameMap>(tagName: K, params: CreateElementParams<K>): HTMLElementTagNameMap[K] {
	let elem: HTMLElementTagNameMap[K] = document.createElement(tagName);

	if (params) {
		if (params.classes)
			elem.classList.add(...params.classes);

		if (params.id)
			elem.id = params.id;

		if (params.style)
			SetStyles(elem, params.style);

		if (params.attrs) {
			//for (let i = 0; i < params.attrs.length; i++)
			for (let attrName in params.attrs) {
				elem.setAttribute(attrName, params.attrs[attrName]);
			}
		}

		if (params.innerHTML)
			elem.innerHTML = params.innerHTML;

		if (params.parentToAppend)
			params.parentToAppend.appendChild(elem);

		if (params.parentToPrepend) {
			if (params.parentToPrepend.firstChild) {
				params.parentToPrepend.insertBefore(elem, params.parentToPrepend.firstChild);
			}
			else {
				params.parentToPrepend.appendChild(elem);
			}
		}

		if (params.childToAppend) {
			elem.appendChild(params.childToAppend);
		}

		if (params.mousedown)
			AddMouseDownListener(elem, params.mousedown);

		if (params.mouseup)
			AddMouseUpListener(elem, params.mouseup);
	}

	return elem;
}



export function TranslateScreen1To1(rect: Rect): Rect {
	return new Rect(rect.x, rect.y, rect.width, rect.height);
}

export function TranslateScreen2To1(rect: Rect): Rect {
	return new Rect(rect.x, rect.y - Settings.ScreenHeight, rect.width, rect.height);
}

export function TranslatePointScreen2To1(point: Point): Point {
	return new Point(point.x, point.y - Settings.ScreenHeight);
}
export function TranslatePointScreen1To1(point: Point): Point {
	return new Point(point.x, point.y);
}

export function TranslateScreen3To1(rect: Rect): Rect {
	return new Rect(
		rect.x,
		rect.y - Settings.ScreenHeight - Settings.ScreenHeight,
		rect.width,
		rect.height
	);
}

export function TranslateScreen2To3(rect: Rect): Rect {
	return new Rect(
		rect.x,
		rect.y + Settings.ScreenHeight,
		rect.width,
		rect.height
	);
}

// function FormatCredit(credit:number):string
// {
// 	return "" + credit + " CREDIT";
// }


// function FormatCurrency(cents: number, currency: string, locale: string = "en-US"): string
// {
// 	let n: number = cents / 100;
// 	return n.toLocaleString(locale, {style: 'currency', currency: currency});
// }

// function FormatCurrentCurrenct(cents: number): string
// {
// 	let n: number = cents / 100;
// 	return n.toLocaleString("en-US", {style: 'currency', currency: "MXN"});
// }
//IN WEBPACK2-MARKO BRANCH DONT EXIST
//  interface Array<T> {
//     random(): T;
// }

//  interface Array<T> {
//     randomIndex(): number;
// }

export function randomInclusive(minInclusive: number, maxInclusive: number): number {
	let range = maxInclusive - minInclusive + 1;
	let rand = minInclusive + Math.floor(Math.random() * range);

	return rand;
}

declare global {
	export interface Array<T> {
		random(): T;
		randomIndex(): number;
	}
}

Array.prototype.random = function () {
	return this[Math.floor((Math.random() * this.length))];
}

Array.prototype.randomIndex = function () {
	return Math.floor((Math.random() * this.length));
}


declare global {
	export interface Number {
		px(): string;
	}
}

Number.prototype.px = function (): string {
	return this.toString() + "px";
}

// Positive modulo
export function mod(n: number, m: number): number {
	return ((n % m) + m) % m;
}

export interface MouseDownEvent {
	type: "mouse" | "touch";
	sourceEvent: MouseEvent | TouchEvent;
	element: HTMLElement;
	target: HTMLElement;
	positionRelativeToElement: Point;
	positionRelativeToScreen: Point;
	positionRelativeToPage: Point;
}

export interface MouseUpEvent {
	type: "mouse" | "touch";
	sourceEvent: MouseEvent | TouchEvent;
	element: HTMLElement;
	target: HTMLElement;
	positionRelativeToElement: Point;
	positionRelativeToScreen: Point;
	positionRelativeToPage: Point;
}

export interface MouseMoveEvent {
	type: "mouse" | "touch";
	sourceEvent: MouseEvent | TouchEvent;
	element: HTMLElement;
	target: HTMLElement;
	positionRelativeToElement: Point;
	positionRelativeToScreen: Point;
	positionRelativeToPage: Point;
}

interface MouseLeaveEvent {
	sourceEvent: MouseEvent | TouchEvent;
	element: HTMLElement;
	target: HTMLElement;
	positionRelativeToElement: Point;
	positionRelativeToScreen: Point;
	positionRelativeToPage: Point;
}

type MouseDownCallback = (ev: MouseDownEvent) => void;
type MouseUpCallback = (ev: MouseUpEvent) => void;
type MouseMoveCallback = (ev: MouseMoveEvent) => void;
type MouseLeaveCallback = (ev: MouseLeaveEvent) => void;

export function AddMouseDownListener(element: HTMLElement, callback: MouseDownCallback) {

	let parameters = { ...{ passive: false } }

	element.addEventListener("mousedown", (ev: MouseEvent) => {

		// if (element["downstate"] != "down")
		// {
		// 	element["downstate"] = "down";
		let mouseDownEvent: MouseDownEvent = {
			type: "mouse",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
			positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
			positionRelativeToPage: new Point(ev.pageX, ev.pageY),
		};


		callback(mouseDownEvent);
		//}
	}, parameters);

	element.addEventListener("touchstart", (ev: TouchEvent) => {

		ev.stopPropagation();
		ev.preventDefault();

		// if (element["downstate"] != "down")
		// {
		// 	element["downstate"] = "down";
		let mouseDownEvent: MouseDownEvent = {
			type: "touch",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.touches[0].clientX, ev.touches[0].clientY),
			positionRelativeToScreen: new Point(ev.touches[0].screenX, ev.touches[0].screenY),
			positionRelativeToPage: new Point(ev.touches[0].pageX, ev.touches[0].pageY),
		};

		callback(mouseDownEvent);
		//}
	}, parameters);
}

export function AddMouseUpListener(element: HTMLElement, callback: MouseUpCallback) {

	let parameters = { ...{ passive: false } }

	element.addEventListener("touchstart", (ev: TouchEvent) => {
		element["state"] = "down";
	});

	element.addEventListener("mousedown", (ev: MouseEvent) => {
		element["state"] = "down";
	});

	element.addEventListener("mouseup", (ev: MouseEvent) => {
		if (element["state"] == "down") {
			element["state"] = "up";
			let mouseUpEvent: MouseUpEvent = {
				type: "mouse",
				sourceEvent: ev,
				element: element,
				target: <HTMLElement>ev.target,
				positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
				positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
				positionRelativeToPage: new Point(ev.pageX, ev.pageY),
			};

			callback(mouseUpEvent);
		}
	}, parameters);

	element.addEventListener("touchend", (ev: TouchEvent) => {
		if (element["state"] == "down") {
			element["state"] = "up";
			let mouseUpEvent: MouseUpEvent = {
				type: "touch",
				sourceEvent: ev,
				element: element,
				target: <HTMLElement>ev.target,

				// no coordinates available when touch ends
				positionRelativeToElement: null,
				positionRelativeToScreen: null,
				positionRelativeToPage: null,
			};

			callback(mouseUpEvent);
		}
	}, parameters);


	element.addEventListener("touchmove", (ev: TouchEvent) => {
		if (element["state"] == "down") {


			if (ev.touches[0].clientX >= element.getBoundingClientRect().left && ev.touches[0].clientX <= element.getBoundingClientRect().right &&
				ev.touches[0].clientY >= element.getBoundingClientRect().top && ev.touches[0].clientY <= element.getBoundingClientRect().bottom) {
			}
			else {
				element["state"] = "up";

				let mouseLeaveEvent: MouseUpEvent = {
					type: "touch",
					sourceEvent: ev,
					element: element,
					target: <HTMLElement>ev.target,
					positionRelativeToElement: new Point(ev.touches[0].clientX, ev.touches[0].clientY),
					positionRelativeToScreen: new Point(ev.touches[0].screenX, ev.touches[0].screenY),
					positionRelativeToPage: new Point(ev.touches[0].pageX, ev.touches[0].pageY),
				};

				callback(mouseLeaveEvent);
			}
		}
	}, parameters);


	element.addEventListener("mouseleave", (ev: MouseEvent) => {
		if (element["state"] == "down") {
			element["state"] = "up";
			let mouseLeaveEvent: MouseUpEvent = {
				type: "mouse",
				sourceEvent: ev,
				element: element,
				target: <HTMLElement>ev.target,
				positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
				positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
				positionRelativeToPage: new Point(ev.pageX, ev.pageY),
			};

			callback(mouseLeaveEvent);
		};
	}, parameters);
}

export function AddMouseMoveListener(element: HTMLElement, callback: MouseMoveCallback, elementToCheck?: HTMLElement) {
	element.addEventListener("touchmove", (ev: TouchEvent) => {
		if (!elementToCheck || elementToCheck["state"] == "down") {
			let mouseLeaveEvent: MouseUpEvent = {
				type: "touch",
				sourceEvent: ev,
				element: element,
				target: <HTMLElement>ev.target,
				positionRelativeToElement: new Point(ev.touches[0].clientX, ev.touches[0].clientY),
				positionRelativeToScreen: new Point(ev.touches[0].screenX, ev.touches[0].screenY),
				positionRelativeToPage: new Point(ev.touches[0].pageX, ev.touches[0].pageY),
			};

			callback(mouseLeaveEvent);
		}
	});

	element.addEventListener("mousemove", (ev: MouseEvent) => {
		if (!elementToCheck || elementToCheck["state"] == "down") {
			let mouseLeaveEvent: MouseUpEvent = {
				type: "mouse",
				sourceEvent: ev,
				element: element,
				target: <HTMLElement>ev.target,
				positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
				positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
				positionRelativeToPage: new Point(ev.pageX, ev.pageY),
			};

			callback(mouseLeaveEvent);
		}
	});
}

export function AddMouseDownUpListener(element: HTMLElement, onmousedown: MouseUpCallback, onmouseup: MouseUpCallback) {
	let parameters = { ...{ passive: false } }

	let buttonPressed = false;

	element.addEventListener("mousedown", (ev: MouseEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseDownEvent: MouseDownEvent = {
			type: "mouse",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
			positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
			positionRelativeToPage: new Point(ev.pageX, ev.pageY),
		};

		if (!buttonPressed) {
			buttonPressed = true;
			onmousedown?.(mouseDownEvent);
		}

	}, parameters);

	element.addEventListener("touchstart", (ev: TouchEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseDownEvent: MouseDownEvent = {
			type: "touch",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.touches[0].clientX, ev.touches[0].clientY),
			positionRelativeToScreen: new Point(ev.touches[0].screenX, ev.touches[0].screenY),
			positionRelativeToPage: new Point(ev.touches[0].pageX, ev.touches[0].pageY),
		};

		if (!buttonPressed) {
			buttonPressed = true;
			onmousedown?.(mouseDownEvent);
		}
	}, parameters);

	element.addEventListener("mouseup", (ev: MouseEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseUpEvent: MouseUpEvent = {
			type: "mouse",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
			positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
			positionRelativeToPage: new Point(ev.pageX, ev.pageY),
		};

		if (buttonPressed) {
			buttonPressed = false;
			onmouseup(mouseUpEvent);
		}
	}, parameters);

	element.addEventListener("touchend", (ev: TouchEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseUpEvent: MouseUpEvent = {
			type: "touch",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: null,
			positionRelativeToScreen: null,
			positionRelativeToPage: null,
		};

		if (buttonPressed) {
			buttonPressed = false;
			onmouseup(mouseUpEvent);
		}
	}, parameters);


	element.addEventListener("touchmove", (ev: TouchEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseLeaveEvent: MouseUpEvent = {
			type: "touch",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: null,
			positionRelativeToScreen: null,
			positionRelativeToPage: null,
		};

		if (ev.touches[0].clientX >= element.getBoundingClientRect().left && ev.touches[0].clientX <= element.getBoundingClientRect().right &&
			ev.touches[0].clientY >= element.getBoundingClientRect().top && ev.touches[0].clientY <= element.getBoundingClientRect().bottom) {
			// touch point is inside element.
			return;
		}

		if (buttonPressed) {
			buttonPressed = false;
			onmouseup(mouseLeaveEvent);
		}




	}, parameters);


	element.addEventListener("mouseleave", (ev: MouseEvent) => {
		ev.preventDefault();
		ev.stopPropagation();

		let mouseLeaveEvent: MouseUpEvent = {
			type: "mouse",
			sourceEvent: ev,
			element: element,
			target: <HTMLElement>ev.target,
			positionRelativeToElement: new Point(ev.offsetX, ev.offsetY),
			positionRelativeToScreen: new Point(ev.screenX, ev.screenY),
			positionRelativeToPage: new Point(ev.pageX, ev.pageY),
		};

		if (buttonPressed) {
			buttonPressed = false;
			onmouseup(mouseLeaveEvent);
		}
	}, parameters);

}

export function shuffle<T>(a: Array<T>): Array<T> {
	let j, x, i;
	for (i = a.length - 1; i > 0; i--) {
		j = Math.floor(Math.random() * (i + 1));
		x = a[i];
		a[i] = a[j];
		a[j] = x;
	}
	return a;
}

export function formatBytes(bytes, decimals = 2) {
	if (bytes === 0) return '0 Bytes';

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));

	return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

export function Measure(child: HTMLElement, parrent: HTMLElement): Point {
	let point: Point = new Point();

	while (child != parrent) {
		point.x += child.offsetLeft;
		point.y += child.offsetTop;

		child = child.offsetParent as HTMLElement;
	}

	return point;
}

export class Matrix {

	/**
	 * Calculate a point transformed with an affine matrix
	 * @param matrix {Matrix} Affine Matrix
	 * @param  point {Point} Point
	 * @returns {Point} Point
	 */
	public static ApplyToPoint(matrix, point) {
		return Array.isArray(point) ? [
			matrix.a * point[0] + matrix.c * point[1] + matrix.e,
			matrix.b * point[0] + matrix.d * point[1] + matrix.f
		] : {
			x: matrix.a * point.x + matrix.c * point.y + matrix.e,
			y: matrix.b * point.x + matrix.d * point.y + matrix.f
		}
	}

	/**
	 * Calculate a translate matrix
	 * @param tx {number} Translation on axis x
	 * @param [ty = 0] {number} Translation on axis y
	 * @returns {Matrix} Affine Matrix
	 */
	public static Translate(tx, ty = 0) {
		return {
			a: 1,
			c: 0,
			e: tx,
			b: 0,
			d: 1,
			f: ty
		}
	}

	/**
	 * Calculate a rotation matrix
	 * @param angle {number} Angle in radians
	 * @param [cx] {number} If (cx,cy) are supplied the rotate is about this point
	 * @param [cy] {number} If (cx,cy) are supplied the rotate is about this point
	 * @returns {Matrix} Affine Matrix
	 */
	public static Rotate(angle, cx, cy) {
		const cosAngle = Math.cos(angle)
		const sinAngle = Math.sin(angle)
		const rotationMatrix = {
			a: cosAngle,
			c: -sinAngle,
			e: 0,
			b: sinAngle,
			d: cosAngle,
			f: 0
		}
		if (Matrix.IsUndefined(cx) || Matrix.IsUndefined(cy)) {
			return rotationMatrix
		}

		return Matrix.Transform([
			Matrix.Translate(cx, cy),
			rotationMatrix,
			Matrix.Translate(-cx, -cy)
		])

	}

	/**
	 * Merge multiple matrices into one
	 * @param matrices {Matrix[]} Array of affine matrix
	 * @returns {Matrix} Affine Matrix
	 */
	public static Transform(...matrices) {
		matrices = Array.isArray(matrices[0]) ? matrices[0] : matrices

		const multiply = (m1, m2) => {
			return {
				a: m1.a * m2.a + m1.c * m2.b,
				c: m1.a * m2.c + m1.c * m2.d,
				e: m1.a * m2.e + m1.c * m2.f + m1.e,
				b: m1.b * m2.a + m1.d * m2.b,
				d: m1.b * m2.c + m1.d * m2.d,
				f: m1.b * m2.e + m1.d * m2.f + m1.f
			}
		}

		switch (matrices.length) {
			case 0:
				throw new Error('no matrices provided')

			case 1:
				return matrices[0]

			case 2:
				return multiply(matrices[0], matrices[1])

			default:
				let [m1, m2, ...rest] = matrices
				let m = multiply(m1, m2)
				return Matrix.Transform(m, ...rest)
		}
	}

	/**
	 * Merge multiple matrices into one
	 * @param matrices {Matrix[]} Array of matrix
	 * @returns {Matrix} Affine Matrix
	 */
	public static Compose(...matrices) {
		return Matrix.Transform(...matrices)
	}

	/**
	 * Calculate a rotation matrix with a DEG angle
	 * @param angle {number} Angle in degree
	 * @param [cx] {number} If (cx,cy) are supplied the rotate is about this point
	 * @param [cy] {number} If (cx,cy) are supplied the rotate is about this point
	 * @returns {Matrix} Affine Matrix
	 */

	public static RotateDEG(angle, cx = undefined, cy = undefined) {
		return Matrix.Rotate(angle * Math.PI / 180, cx, cy)
	}

	/**
	 * Calculate a scaling matrix
	 * @param sx {number} Scaling on axis x
	 * @param [sy = sx] {number} Scaling on axis y (default sx)
	 * @returns {Matrix} Affine Matrix
	 */
	public static Scale(sx, sy = undefined) {
		if (Matrix.IsUndefined(sy)) sy = sx
		return {
			a: sx,
			c: 0,
			e: 0,
			b: 0,
			d: sy,
			f: 0
		}
	}

	protected static IsUndefined(val) {
		return typeof val === 'undefined'
	}

	public static ToString(matrix) {
		return `matrix(${matrix.a},${matrix.b},${matrix.c},${matrix.d},${matrix.e},${matrix.f})`
	}

	public static ToCSS(matrix) {
		return Matrix.ToString(matrix)
	}
}



export class Compare {
	public static NumDesc(a: number, b: number): number {
		return b - a;
	}

	public static NumAsc(a: number, b: number): number {
		return a - b;
	}
}

export class Filter {
	public static UniqueItem(value, index, arr) {
		return arr.indexOf(value) == index
	}

	public static UniqueBy<T>(propertyExtractor: (obj: T) => any): (value: T, index: number, arr: T[]) => boolean {
		return (value: T, index: number, arr: T[]): boolean => {
			const extractedValue = propertyExtractor(value);
			return arr.findIndex(item => propertyExtractor(item) === extractedValue) === index;
		};
	}

	// public static ObjByProperty<K, V extends Record<K, V>>(key: keyof K, property: string): (obj: K) => boolean {
	// 	return (obj: K): boolean => {
	// 	  const value = obj[key];
	// 	  return value && typeof value === "object" && property in value;
	// 	};
	// }



	// public static Feature<K extends string, V, N extends V>(record: Record<K, V>, predicate: (value: V, key: K) => value is N): Record<K, N> {
	// 	return Object.fromEntries(
	// 		Object.entries(record).filter(([key, value]) => predicate(value as V, key as K))
	// 	) as Record<K, N>;
	// }
}

export function FilterFeatures<K extends string, V, N extends V>(record: Record<K, V>, predicate: (value: V, key: K) => value is N): Record<K, N> {
	return Object.fromEntries(
		Object.entries(record).filter(([key, value]) => predicate(value as V, key as K))
	) as Record<K, N>;
}

export function IsEmptyOrNull<K extends string, V>(record: Record<K, V>): boolean {
	if (!record) {
		return true;
	}

	return Object.keys(record).length == 0;
}


export type CompareFunc<T> = (a: T, b: T) => number;
export type GetterFunc<T, R> = (item: T) => R;

export function CompareAsc<T, R>(getter: (i: T) => R): CompareFunc<T> {
	let compareFunc: CompareFunc<T> = function (a: T, b: T) {
		let aval: R = getter(a);
		let bval: R = getter(b);

		if (aval < bval) return -1;
		else if (aval > bval) return 1;
		else return 0;
	}

	return compareFunc;
}

export function CompareDesc<T, R>(getter: (i: T) => R): CompareFunc<T> {
	let compareFunc: CompareFunc<T> = function (a: T, b: T) {
		let aval: R = getter(a);
		let bval: R = getter(b);

		if (aval < bval) return 1;
		else if (aval > bval) return -1;
		else return 0;
	}

	return compareFunc;
}



export class Scale {
	protected static get scale(): number {
		return Settings.ScreenHeight / 1080;
	}

	public static readonly ZERO_PX: string = "0px";

	public static X(x: number): number {
		return x * this.scale;
	}

	public static Xpx(x: number): string {
		return (x * this.scale) + "px";
	}

	public static Y(y: number): number {
		return y * this.scale;
	}

	public static Ypx(y: number): string {
		return (y * this.scale) + "px";
	}

	public static W(w: number): number {
		return w * this.scale;
	}

	public static Wpx(w: number): string {
		return (w * this.scale) + "px";
	}

	public static H(h: number): number {
		return h * this.scale;
	}

	public static Hpx(h: number): string {
		return (h * this.scale) + "px";
	}

	public static XY(xy: number): number {
		return xy * this.scale;
	}

	public static XYpx(xy: number): string {
		return (xy * this.scale) + "px";
	}

	// font height
	public static fh(h: number): number {
		return h * this.scale;
	}

	// font height, px
	public static fhpx(h: number): string {
		return (h * this.scale) + "px";
	}
}

//DONT exist in Marko branch 
// interface Math
// {
//     roundTo(num: number, to: number);
// }


declare global {
	export interface Math {
		roundTo(num: number, to: number);
	}
}




Math.roundTo = function (num: number, to: number) {
	let factor: number = Math.pow(10, to);
	return Math.round((num + Number.EPSILON) * factor) / factor;
}

export function Merge(a, b) {
	let merged = { ...a };
	for (let key in b) {
		if (merged[key] === undefined || merged[key] === null) {
			merged[key] = b[key];
		}
	}

	return merged;
}


export async function Delay(milliseconds: number): Promise<void> {
	return new Promise<void>((resolve) => setTimeout(() => resolve(), milliseconds));
}

// fire and forget
export function NextTask(task: () => void, frames: number = 1) {
	setTimeout(task, 33 * 1);
}

// fire and resolve
export async function NextTaskAsync(task: () => void, frames: number = 1): Promise<void> {
	return new Promise(function nextTaskExecutorPromise(resolve, reject) {
		setTimeout(function nextTaskExecutor() {
			task();
			resolve();
		}, 33 * 1);
	});
}


export function Safe(f: () => void): boolean {
	try {
		f();
		return true;
	}
	catch { }

	return false;
}

export async function SafeAsync<T>(f: () => Promise<T>): Promise<void>
{
	try
	{
		await f();
	}
	catch {}
}

export function isTouchEvent(e: TouchEvent | MouseEvent): e is TouchEvent {
	return e && 'touches' in e;
}

export function isMouseEvent(e: TouchEvent | MouseEvent): e is MouseEvent {
	return e && 'screenX' in e;
}
//** its use for create simple img from symbols , will return some array whit img inside  */
export async function DrawSymbolImage(resourceManager: IReadOnlyResourceManager, symbolNames: string[], symbolsSize: Size, symbolPath: string, middleImage: boolean = false): Promise<Map<string, HTMLElement>>
{
	let symbolsNameMap: Map<string, HTMLElement> = new Map<string, HTMLElement>();
	for (const name of symbolNames)
	{

		//take symbol from resources manager
		let sprite = resourceManager.Get<SpriteResource>(symbolPath + "-" + name).sprite;

		let symbolsSprite = new CanvasSprite({
			sprite: sprite.Copy(),
			canvasSize: symbolsSize
		});


		symbolsSprite.Update(true);

		//return data saved from  img from canvas
		let dataCanvas = await symbolsSprite.PreviewImage(middleImage);
		//let spanImg = CreateElement("span", {});

		//create img div empty
		let imgConverted = CreateElement("img", {
			style: {
				width: symbolsSize.Width + "px",
				height: symbolsSize.Height + "px",
				backgroundPosition: "center",
				backgroundSize: "contain",
				backgroundOrigin: "center",
			}
		});
		//add img src from data img saved 
		imgConverted.src = dataCanvas;
		symbolsNameMap.set(name, imgConverted)

		//put everything null for clean memory 
		sprite = null;
		symbolsSprite = null;
		dataCanvas = null
		//spanImg = null;
		imgConverted = null;
	};

	return symbolsNameMap;
}

//** its use for create simple img from symbols spine , will img html converted from spine   */
export async function DrawSymbolImageSpine(skeleton: ISkeletonData, symbolsSize: Size) {
	let spine = CreateSpineResources(skeleton);	

	let showAward = true;
	if (spine.skeleton.findBone("hideAward")) {
		showAward = false;
	}

	//spine.width = this.spinnableReels.GetSymbolSize().width;
	//spine.height = this.spinnableReels.GetSymbolSize().height;
	let scaleWidth = symbolsSize.Width / spine.width;
	let scaleHeight = symbolsSize.Height / spine.height
	let scale = Math.min(scaleWidth, scaleHeight);
	spine.width = spine.width * scale;
	spine.height = spine.height * scale;
	spine.position.set(0, 0);
	//spine.update(0);
	let renderTexture = PIXI.RenderTexture.create({ width: spine.width, height: spine.height });
	// Render Spine object onto render texture	
	globalThis.renderSymbol.render(spine, { renderTexture: renderTexture });
	// Create a new Sprite using the RenderTexture                
	// Extract the canvas                
	let canvas = (globalThis.renderSymbol.plugins.extract.canvas(new PIXI.Sprite(renderTexture)) as HTMLCanvasElement);

	//let img = globalThis.renderSymbol.plugins.extract.img()
	// //create img div empty
	let imgConverted = CreateElement("img", {
		style: {
			width: spine.width + "px",
			height: spine.height + "px",
			backgroundPosition: "center",
			backgroundSize: "contain",
			backgroundOrigin: "center",
		}
	});
	
	// Convert canvas to blob instead of base64 for better performance
	const blob = await new Promise<Blob>((resolve, reject) => {
		canvas.toBlob((blob) => {
			if (blob) resolve(blob);
			else reject(new Error("Failed to create blob"));
		}, "image/png", 0.9); // Slightly reduce quality for better performance
	});
	
	const objectURL = URL.createObjectURL(blob);
	imgConverted.src = objectURL;
	
	// Store the URL for cleanup later
	(imgConverted as any)._objectURL = objectURL;

	spine.autoUpdate = false;
	globalThis.renderSymbol.clear();
	//globalThis.renderSymbol.destroy(true);
	renderTexture.destroy(true);
	renderTexture = null;
	spine.destroy({children: true, texture: false, baseTexture: true});
	return imgConverted;
}



//funtion for create spine , need to send the json file 
/** Funtion for create spine , need to send the json file 
 *  jsonSpine : its the skeletonData , scaleSpine its simple number whitout scale , insid its doing the scale */
export function CreateSpinePixelLeesResources(jsonSpine: ISkeletonData, scale: boolean = true, scaleSpine: number = 1, scaleMode = PIXI.MIPMAP_MODES.ON): Spine {
	let spine = new Spine(jsonSpine);

	// ✅ Apply high-quality filtering for each texture
	spine.slotContainers.forEach((slotContainer) => {
		slotContainer.children.forEach((child) => {
			if (child instanceof PIXI.Sprite && child.texture.baseTexture) {
				child.texture.baseTexture.scaleMode = PIXI.SCALE_MODES.LINEAR; // Smooth scaling
				child.texture.baseTexture.mipmap = scaleMode; // Better downscaling
			}
		});
	});

	if (scale == true) spine.scale.set(Scale.XY(scaleSpine));
	return spine;
}


const originalGraphicsDestroy = PIXI.Graphics.prototype.destroy;
PIXI.Graphics.prototype.destroy = function (options?: boolean | PIXI.IDestroyOptions) {

	if ((this as any).destroyed) {
		return;
	}

	let name = this.name;

	if (!name)
	{
		name = new Error().stack;
	}
	ClearCache(this);
	gsap.killTweensOf(this);
	if(IsDebug())console.log(`Destroying Graphics: ${name}`);
	originalGraphicsDestroy.call(this, options);
	if(IsDebug())setTimeout(() => (this as any).__destroyedName = name, 1000);
};



const originalSpriteDestroy = PIXI.Sprite.prototype.destroy;
PIXI.Sprite.prototype.destroy = function (options?: boolean | PIXI.IDestroyOptions) {

	if ((this as any).destroyed) return;

	let name = this.name;

	if (!name)
	{
		name = new Error().stack;
	}
	ClearCache(this);
	gsap.killTweensOf(this);
	if(IsDebug())console.log(`Destroying Sprite: ${name}`);
	originalSpriteDestroy?.call(this, options);
	if(IsDebug())setTimeout(() => (this as any).__destroyedName = name, 1000);
};



const originalContainerDestroy = PIXI.Container.prototype.destroy;
PIXI.Container.prototype.destroy = function (options?: boolean | PIXI.IDestroyOptions) {

	let name = this.name;

	if (!name)
	{
		name = new Error().stack;
	}
	ClearCache(this);
	gsap.killTweensOf(this);
	if(IsDebug())console.log(`Destroying Container: ${name}`);
	originalContainerDestroy.call(this, options);
	if(IsDebug())setTimeout(() => (this as any).__destroyedName = name, 1000);
};

const originalSpineDestroy = Spine.prototype.destroy;
Spine.prototype.destroy = function (options?: boolean | PIXI.IDestroyOptions) {

	let name = this.name;

	if (!name)
	{
		name = new Error().stack;
	}

	

	gsap.killTweensOf(this);
	if(IsDebug())console.log(`Destroying Spine: ${name}`);
	originalSpineDestroy.call(this, options =  { baseTexture : false , texture : false , children : true});
	if(IsDebug())setTimeout(() => (this as any).__destroyedName = name, 1000);
};

function ClearCache(obj){
	//Desactivar cache (restaura hijos)
    if (obj.cacheAsBitmap) {
        obj.cacheAsBitmap = false;
    }

    // Cache interno
    const data = obj._cacheData;
    if (!data) return;

    // Destruir el sprite temporal
    data.sprite?.destroy(true);

    //Destruir RenderTexture
    data.renderTexture?.destroy(true);

    // Destruir baseTexture
    data.texture?.baseTexture?.destroy(true);

    //Limpiar referencia
    obj._cacheData = null;
}


export function CreateSpineResources(jsonSpine: ISkeletonData, scale: boolean = true, scaleSpine: number = 1): Spine {
	let spine = new Spine(jsonSpine);
	if (scale == true) spine.scale.set(Scale.XY(scaleSpine));
	return spine;
}

export function DestroySpine(spine: Spine, options?: boolean | PIXI.IDestroyOptions)
{	
	(spine as any)._destroyedName = spine.name; // Preserve the name before destroying
	spine.destroy(options);
}

const currentUrl = new URL(window.location.href);
const currentParams = new URLSearchParams(currentUrl.search);

export function GetUrlParamBool(name: string, defaultValue: boolean): boolean {
	let url = new URL(window.location.href);
	let params = new URLSearchParams(url.search);

	const paramValue = params.get(name);
	if (paramValue) {
		return paramValue.toLowerCase() == "true";
	}

	return defaultValue;
}

export function GetUrlParamString(name: string, defaultValue: string = null): string
{
	const paramValue = currentParams.get(name);
	if (paramValue !== null)
	{
		return paramValue;
	}

	return defaultValue;
}


const debugParamSet = GetUrlParamBool("debug", false);
export function IsDebug(): boolean {
	return debugParamSet;
}


export function SetNullReference(ob: any) {
	Object.keys(ob).forEach(key => {
		(ob as any)[key] = null;
	});
}


export class MyPIXIContainer extends PIXI.Container {
	//protected nameContainer: string = null;

	protected ondestroy?: () => void;
	constructor(name: string, ondestroy?: () => void)
	{
		super();
		this.name = name;
		//this.nameContainer = name;
	}

	override destroy(options?: boolean | PIXI.IDestroyOptions)
	{
		this.ondestroy?.();
		super.destroy(options);
	}
}


export function CreateTexturePIXISprite(dialogTexturesDecoded: DecodedImageResource) {
	
	//dialogTextureBasePixi.scaleMode = PIXI.SCALE_MODES.NEAREST;
	return PIXI.Texture.from(dialogTexturesDecoded.DecodedImage());

}

export function debuggerSpine(spine: Spine){
        // 👇 Activa el renderer de debug
        const debugRenderer = new SpineDebugRenderer();
        debugRenderer.drawBones = true;
        //debugRenderer.drawSlots = true;
        debugRenderer.drawPaths = true;
        debugRenderer.drawBoundingBoxes = true;
        debugRenderer.pathsLineColor = 0xff0000;
        debugRenderer.pathsCurveColor = 0x00ff00;
        debugRenderer.drawDebug = true;

        // Asignar como renderer de debug
        spine.debug = debugRenderer;
}


export function CreditMultiply(credit: Credit, multiplier: number): Credit {
	return {
		Value: credit.Value * multiplier
	};
}

export function SetScaleSpriteTexture(sprite: PIXI.Sprite, width: number, height: number): void {
	let scaleWidth = Scale.W(width) / sprite.texture.width;
	let scaleHeight = Scale.H(height) / sprite.texture.height
	let scaleProm = (scaleHeight + scaleWidth) / 2;
	sprite.width = sprite.texture.width * scaleProm;
	sprite.height = sprite.texture.height * scaleProm;
}




// export function CreatePixiTexture<R extends Resource = PIXI.Resource, RO = any>(name: string, source: PIXI.TextureSource | TextureSource[], options?: IBaseTextureOptions<RO>, strict?: boolean): Texture<R>
// {
// 	const texture = PIXI.Texture.from(source, options);
// 	texture.id
// }
